import { useCallback, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';

export const useTestCases = () => {
  const supabase = useSupabaseClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTestCases = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching test cases from test_cases_with_hierarchy...');
      const { data, error: fetchError } = await supabase
        .from('test_cases_with_hierarchy')
        .select('*')
        .order('group_order')
        .order('item_order')
        .order('priority', { ascending: false });

      if (fetchError) {
        console.error('Error fetching test cases:', fetchError);
        throw fetchError;
      }
      console.log('Successfully fetched test cases:', data);
      return data;
    } catch (err) {
      console.error('Error in fetchTestCases:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching summary from test_case_hierarchy...');
      const { data, error: fetchError } = await supabase
        .from('test_case_hierarchy')
        .select('*')
        .order('group_order')
        .order('item_order');

      if (fetchError) {
        console.error('Error fetching summary:', fetchError);
        throw fetchError;
      }
      console.log('Successfully fetched summary:', data);
      return data;
    } catch (err) {
      console.error('Error in fetchSummary:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  const updateTestCase = useCallback(async (id, updates) => {
    try {
      setLoading(true);
      setError(null);
      
      // If we're updating the status, add the last tested info
      const { data: { user } } = await supabase.auth.getUser();
      
      // Remove fields that aren't in the database
      const { 
        group_order, 
        item_order, 
        id: testId, 
        created_at, 
        updated_at,
        menu_group_id,
        menu_group_name,
        menu_item_id,
        menu_item_name,
        ...cleanUpdates 
      } = updates;
      
      // Only send the fields that are in the database schema
      const dbFields = {
        test_name: cleanUpdates.test_name,
        description: cleanUpdates.description,
        steps: cleanUpdates.steps,
        expected_result: cleanUpdates.expected_result,
        priority: cleanUpdates.priority,
        status: cleanUpdates.status
      };
      
      const finalUpdates = cleanUpdates.status ? {
        ...dbFields,
        last_tested_at: new Date().toISOString(),
        last_tested_by: user?.id
      } : dbFields;
      
      console.log('Updating test case with id:', id, 'and updates:', finalUpdates);
      const { data, error: updateError } = await supabase
        .from('test_cases')
        .update(finalUpdates)
        .eq('id', id)
        .select('*')
        .single();

      if (updateError) {
        console.error('Error updating test case:', updateError);
        throw updateError;
      }
      console.log('Successfully updated test case:', data);
      return data;
    } catch (err) {
      console.error('Error in updateTestCase:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  const addTestCase = useCallback(async (testCase) => {
    try {
      setLoading(true);
      setError(null);
      
      // Ensure we have a menu_item_id
      if (!testCase.menu_item_id) {
        throw new Error('menu_item_id is required');
      }
      
      console.log('Adding new test case:', testCase);
      const { data, error: insertError } = await supabase
        .from('test_cases')
        .insert([testCase])
        .select('*')
        .single();

      if (insertError) {
        console.error('Error adding test case:', insertError);
        throw insertError;
      }
      console.log('Successfully added test case:', data);
      return data;
    } catch (err) {
      console.error('Error in addTestCase:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  return {
    fetchTestCases,
    fetchSummary,
    updateTestCase,
    addTestCase,
    loading,
    error
  };
};
